import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ClipboardList, AlertTriangle, CheckCircle2, Clock, FolderKanban,
  Search, ChevronRight, Camera, Wrench, Image as ImageIcon, Activity,
  ArrowLeft, Send,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, KPI, Spinner, EmptyState, Input } from "@/components/ui";
import { format, parseISO, formatDistanceToNow } from "date-fns";

// =============================================================================
// HopCentreScreen — /acceptance
// T-010 C.3c — cross-project HOP review hub.
//
// Three tabs:
//   * My packs        — packs I've uploaded into (default for field / subbie)
//   * Awaiting review — status='submitted' across all visible projects
//                       (default for manager / director)
//   * Recently accepted — status='accepted' in the last 30 days
//
// Each card links into /acceptance/:packId for the full review surface
// (photo grid + lightbox + download + manager actions). Cards also link
// out to /projects/:projectId/hop/:packId for the existing project-context
// structural overview.
//
// RLS does the heavy lifting: hop_packs_read = manager+ OR is_on_project,
// so the queries here just SELECT and let the database filter.
// =============================================================================

type PackStatus = "open" | "submitted" | "accepted" | "defects_raised";
type TabKey = "board" | "open" | "mine" | "awaiting" | "recent" | "sent";

// Board lanes — the "see every pack and its status at a glance" order.
const BOARD_LANES: { status: PackStatus; label: string }[] = [
  { status: "defects_raised", label: "Defects raised — action needed" },
  { status: "submitted",      label: "Awaiting review" },
  { status: "open",           label: "In progress (capturing)" },
  { status: "accepted",       label: "Accepted" },
];

// T-220 — submission row shape (separate from PackRow since hop_submissions
// has different fields). Used by the "Sent to client" tab.
interface SubmissionRow {
  submission_id:    string;
  submission_number: string;
  title:            string | null;
  status:           "draft" | "sent" | "opened" | "downloaded" | "signed" | "expired" | "cancelled";
  sent_at:          string | null;
  expires_at:       string | null;
  first_opened_at:  string | null;
  first_downloaded_at: string | null;
  signed_at:        string | null;
  magic_token:      string | null;
  project?:         { project_id: string; project_code: string; project_name: string } | null;
  recipient_count?: number;
}

interface PackRow {
  pack_id:           string;
  pack_number:       string;
  title:             string | null;
  status:            PackStatus;
  label_prefix:      string | null;
  created_at:        string;
  submitted_at:      string | null;
  accepted_at:       string | null;
  defects_raised_at: string | null;
  project?:          { project_id: string; project_code: string; project_name: string; site?: { site_name: string | null } | null } | null;
  template?:         { template_code: string; display_name: string } | null;
  asset_count?:      number;
}

const PACK_TONE: Record<PackStatus, "open" | "pending" | "approved" | "rejected"> = {
  open:           "open",
  submitted:      "pending",
  accepted:       "approved",
  defects_raised: "rejected",
};

const PACK_LABEL: Record<PackStatus, string> = {
  open:           "in progress",
  submitted:      "awaiting review",
  accepted:       "accepted",
  defects_raised: "defects raised",
};

// -----------------------------------------------------------------------------

export default function HopCentreScreen() {
  const user = useRequireAuth();
  const isManager = hasRole(user, "manager");

  // Tab can be driven by ?tab= query param (the mobile Camera button uses
  // ?tab=open to land users straight on live in-progress packs).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab") as TabKey | null;
  // Default tab depends on role: managers triage 'awaiting', field sees 'mine'
  const [tab, setTabState] = useState<TabKey>(
    urlTab && ["board","open","mine","awaiting","recent","sent"].includes(urlTab)
      ? urlTab
      : (isManager ? "board" : "mine")
  );
  const [search, setSearch] = useState("");

  function setTab(next: TabKey) {
    setTabState(next);
    // Mirror tab into the URL so deep-links work + browser back is sensible.
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  }

  // Re-set default if role changes (e.g. after login) UNLESS the URL pinned a tab.
  useEffect(() => {
    if (!urlTab) setTabState(isManager ? "board" : "mine");
  }, [isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- KPI tiles (cross-project, role-scoped via RLS) ----------------------
  const { data: kpis } = useQuery({
    queryKey: ["hop_centre_kpis"],
    queryFn: async () => {
      const since30d = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const since7d  = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [openRes, awaitingRes, defectsRes, acceptedRes, photosRes, recentlyAcceptedRes] = await Promise.all([
        supabase.from("hop_packs").select("pack_id", { count: "exact", head: true }).eq("status", "open"),
        supabase.from("hop_packs").select("pack_id", { count: "exact", head: true }).eq("status", "submitted"),
        supabase.from("hop_packs").select("pack_id", { count: "exact", head: true }).eq("status", "defects_raised"),
        supabase.from("hop_packs").select("pack_id", { count: "exact", head: true })
          .eq("status", "accepted").gte("accepted_at", since30d),
        supabase.from("hop_assets").select("asset_id", { count: "exact", head: true })
          .eq("asset_type", "photo").gte("captured_at", since7d),
        // For avg time-to-accept — pull submitted_at + accepted_at on recently
        // accepted packs. Aggregate client-side (cheaper than a Postgres
        // function call per render).
        supabase.from("hop_packs")
          .select("submitted_at, accepted_at")
          .eq("status", "accepted").gte("accepted_at", since30d)
          .not("submitted_at", "is", null),
      ]);

      // Compute avg hours between submitted_at and accepted_at
      const samples = (recentlyAcceptedRes.data ?? []) as Array<{ submitted_at: string; accepted_at: string }>;
      let avgHours: number | null = null;
      if (samples.length > 0) {
        const totalMs = samples.reduce((sum, r) => {
          const sub = new Date(r.submitted_at).getTime();
          const acc = new Date(r.accepted_at).getTime();
          return sum + Math.max(0, acc - sub);
        }, 0);
        avgHours = Math.round((totalMs / samples.length) / (1000 * 60 * 60));
      }

      return {
        open:     openRes.count ?? 0,
        awaiting: awaitingRes.count ?? 0,
        defects:  defectsRes.count ?? 0,
        accepted: acceptedRes.count ?? 0,
        photos7d: photosRes.count ?? 0,
        avgHoursToAccept: avgHours,
      };
    },
  });

  // -- Board: every live pack across all projects, one screen, grouped by
  //    status. This is the manager "see what's where" tracking hub. -----------
  const { data: boardPacks = [], isLoading: loadingBoard } = useQuery({
    queryKey: ["hop_centre_board"],
    enabled: tab === "board",
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`pack_id, pack_number, title, status, label_prefix, created_at,
                 submitted_at, accepted_at, defects_raised_at,
                 project:project_id(project_id, project_code, project_name, site:site_id(site_name)),
                 template:template_id(template_code, display_name)`)
        .in("status", ["open", "defects_raised", "submitted", "accepted"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      const packs = (data ?? []) as unknown as PackRow[];
      if (packs.length === 0) return packs;
      // Hydrate photo counts in one sweep.
      const ids = packs.map(p => p.pack_id);
      const { data: assetRows } = await supabase
        .from("hop_assets").select("pack_id").in("pack_id", ids);
      const counts = new Map<string, number>();
      for (const a of (assetRows ?? []) as Array<{ pack_id: string }>) {
        counts.set(a.pack_id, (counts.get(a.pack_id) ?? 0) + 1);
      }
      return packs.map(p => ({ ...p, asset_count: counts.get(p.pack_id) ?? 0 }));
    },
  });

  // -- My packs (where I've uploaded an asset) ------------------------------
  const { data: myPacks = [], isLoading: loadingMine } = useQuery({
    queryKey: ["hop_centre_my", user?.employee_id],
    enabled: !!user?.employee_id && tab === "mine",
    queryFn: async () => {
      // Find pack_ids where I've contributed at least one asset
      const { data: assetRows } = await supabase
        .from("hop_assets")
        .select("pack_id")
        .eq("captured_by_employee_id", user!.employee_id!);
      const packIds = [...new Set((assetRows ?? []).map(r => r.pack_id))];
      if (packIds.length === 0) return [];

      const { data, error } = await supabase
        .from("hop_packs")
        .select(`pack_id, pack_number, title, status, label_prefix, created_at,
                 submitted_at, accepted_at, defects_raised_at,
                 project:project_id(project_id, project_code, project_name),
                 template:template_id(template_code, display_name)`)
        .in("pack_id", packIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PackRow[];
    },
  });

  // -- Open / Live (status=open, ordered by most recent asset upload so the
  //    actively-being-worked packs float to the top — managers see live progress,
  //    field crew find their current pack quickly) ----------------------------
  const { data: openPacks = [], isLoading: loadingOpen } = useQuery({
    queryKey: ["hop_centre_open"],
    enabled: tab === "open",
    // Live refresh while the Open tab is visible so managers see uploads as they
    // land. 30s is a reasonable cadence for "what's the crew doing right now"
    // without hammering the DB.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`pack_id, pack_number, title, status, label_prefix, created_at,
                 submitted_at, accepted_at, defects_raised_at,
                 project:project_id(project_id, project_code, project_name),
                 template:template_id(template_code, display_name)`)
        .in("status", ["open", "defects_raised"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      const packs = (data ?? []) as unknown as PackRow[];
      if (packs.length === 0) return packs;

      // Hydrate per-pack asset count + last upload timestamp in one sweep.
      const ids = packs.map(p => p.pack_id);
      const { data: assetRows } = await supabase
        .from("hop_assets")
        .select("pack_id, captured_at")
        .in("pack_id", ids);
      const stats = new Map<string, { count: number; lastAt: string | null }>();
      for (const a of (assetRows ?? []) as Array<{ pack_id: string; captured_at: string | null }>) {
        const cur = stats.get(a.pack_id) ?? { count: 0, lastAt: null };
        cur.count += 1;
        if (a.captured_at && (!cur.lastAt || a.captured_at > cur.lastAt)) cur.lastAt = a.captured_at;
        stats.set(a.pack_id, cur);
      }
      const enriched = packs.map(p => ({
        ...p,
        asset_count: stats.get(p.pack_id)?.count ?? 0,
        last_asset_at: stats.get(p.pack_id)?.lastAt ?? null,
      }));
      // Sort: packs with recent asset activity first, then by created_at.
      enriched.sort((a, b) => {
        const aT = (a as any).last_asset_at ?? a.created_at;
        const bT = (b as any).last_asset_at ?? b.created_at;
        return bT.localeCompare(aT);
      });
      return enriched;
    },
  });

  // -- Awaiting review (status=submitted, oldest first — queue fairness) ----
  const { data: awaitingPacks = [], isLoading: loadingAwaiting } = useQuery({
    queryKey: ["hop_centre_awaiting"],
    enabled: tab === "awaiting",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`pack_id, pack_number, title, status, label_prefix, created_at,
                 submitted_at, accepted_at, defects_raised_at,
                 project:project_id(project_id, project_code, project_name),
                 template:template_id(template_code, display_name)`)
        .eq("status", "submitted")
        .order("submitted_at", { ascending: true });   // oldest first
      if (error) throw error;
      return (data ?? []) as unknown as PackRow[];
    },
  });

  // -- Recently accepted (last 30 days, newest first) -----------------------
  const { data: recentPacks = [], isLoading: loadingRecent } = useQuery({
    queryKey: ["hop_centre_recent"],
    enabled: tab === "recent",
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`pack_id, pack_number, title, status, label_prefix, created_at,
                 submitted_at, accepted_at, defects_raised_at,
                 project:project_id(project_id, project_code, project_name),
                 template:template_id(template_code, display_name)`)
        .eq("status", "accepted")
        .gte("accepted_at", since)
        .order("accepted_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PackRow[];
    },
  });

  // T-220 — Sent to client (hop_submissions, includes draft + sent + opened
  // + downloaded so manager can find any in-flight submission). Newest first.
  const { data: sentSubmissions = [], isLoading: loadingSent } = useQuery({
    queryKey: ["hop_centre_sent"],
    enabled: tab === "sent",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_submissions")
        .select(`
          submission_id, submission_number, title, status,
          sent_at, expires_at, first_opened_at, first_downloaded_at, signed_at,
          magic_token,
          project:project_id(project_id, project_code, project_name)
        `)
        .in("status", ["sent", "opened", "downloaded", "signed", "draft"])
        .order("sent_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as SubmissionRow[];
    },
  });

  const activePacks = tab === "open" ? openPacks
                    : tab === "mine" ? myPacks
                    : tab === "awaiting" ? awaitingPacks
                    : tab === "recent" ? recentPacks
                    : [];   // 'sent' tab uses sentSubmissions, not packs
  const loading = tab === "board" ? loadingBoard
                : tab === "open" ? loadingOpen
                : tab === "mine" ? loadingMine
                : tab === "awaiting" ? loadingAwaiting
                : tab === "recent" ? loadingRecent
                : loadingSent;

  // Board search filter (separate from the flat-tab activePacks path).
  const filteredBoard = useMemo(() => {
    if (!search.trim()) return boardPacks;
    const q = search.trim().toLowerCase();
    return boardPacks.filter(p =>
      p.pack_number.toLowerCase().includes(q)
      || (p.title?.toLowerCase().includes(q) ?? false)
      || (p.label_prefix?.toLowerCase().includes(q) ?? false)
      || (p.project?.project_code.toLowerCase().includes(q) ?? false)
      || (p.project?.project_name.toLowerCase().includes(q) ?? false)
      || (p.project?.site?.site_name?.toLowerCase().includes(q) ?? false)
    );
  }, [boardPacks, search]);

  // -- Search filter --------------------------------------------------------
  const filteredPacks = useMemo(() => {
    if (!search.trim()) return activePacks;
    const q = search.trim().toLowerCase();
    return activePacks.filter(p =>
      p.pack_number.toLowerCase().includes(q)
      || (p.title?.toLowerCase().includes(q) ?? false)
      || (p.label_prefix?.toLowerCase().includes(q) ?? false)
      || (p.project?.project_code.toLowerCase().includes(q) ?? false)
      || (p.project?.project_name.toLowerCase().includes(q) ?? false)
    );
  }, [activePacks, search]);

  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-20">
      <Link
        to="/project"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14}/> Project portal
      </Link>

      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ClipboardList size={20} className="text-accent" />
            Acceptance Centre
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Cross-project control hub for HOP review, submissions and defect tracking.{" "}
            {isManager
              ? "Triage submissions, accept clean packs, raise defects on the rest."
              : "Track packs you've contributed to and see what's been accepted."}
          </p>
        </div>
        <Link
          to="/acceptance/defects"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-line bg-ink-raised hover:border-accent hover:text-accent text-text-muted text-[12px] font-medium transition-colors flex-shrink-0"
        >
          <Wrench size={13}/> Defect Register
          <ChevronRight size={12}/>
        </Link>
      </div>

      {/* KPI tiles — six tiles for fuller dashboard feel */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <KPI
          label="In progress"
          value={kpis?.open ?? "—"}
          icon={Activity}
          sub="open packs"
        />
        <KPI
          label="Awaiting review"
          value={kpis?.awaiting ?? "—"}
          icon={Clock}
          accent={!!kpis && kpis.awaiting > 0}
          sub="submitted"
        />
        <KPI
          label="Defects raised"
          value={kpis?.defects ?? "—"}
          icon={AlertTriangle}
          accent={!!kpis && kpis.defects > 0}
          sub="needs rework"
        />
        <KPI
          label="Accepted"
          value={kpis?.accepted ?? "—"}
          icon={CheckCircle2}
          sub="last 30 days"
        />
        <KPI
          label="Photos"
          value={kpis?.photos7d ?? "—"}
          icon={ImageIcon}
          sub="last 7 days"
        />
        <KPI
          label="Avg review time"
          value={kpis?.avgHoursToAccept != null
            ? (kpis.avgHoursToAccept >= 24
                ? `${Math.round(kpis.avgHoursToAccept / 24)}d`
                : `${kpis.avgHoursToAccept}h`)
            : "—"}
          icon={Activity}
          sub="submit → accept"
        />
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit overflow-x-auto">
        <TabBtn label="Board"             active={tab === "board"}    onClick={() => setTab("board")} />
        <TabBtn label="Open"              active={tab === "open"}     onClick={() => setTab("open")}     count={kpis?.open} />
        <TabBtn label="My packs"          active={tab === "mine"}     onClick={() => setTab("mine")} />
        <TabBtn label="Awaiting review"   active={tab === "awaiting"} onClick={() => setTab("awaiting")} count={kpis?.awaiting} />
        <TabBtn label="Sent to client"    active={tab === "sent"}     onClick={() => setTab("sent")}    count={sentSubmissions.length || undefined} />
        <TabBtn label="Recently accepted" active={tab === "recent"}   onClick={() => setTab("recent")} />
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by pack #, project, label prefix…"
          className="pl-9"
        />
      </div>

      {/* Pack list */}
      {loading ? (
        <div className="p-12 flex justify-center"><Spinner size={20}/></div>
      ) : tab === "board" ? (
        filteredBoard.length === 0 ? (
          <Card>
            <EmptyState
              title="No live HOP packs"
              description="Every pack is closed or none exist yet. New packs show here the moment field crew start capturing."
              icon={ClipboardList}
            />
          </Card>
        ) : (
          <div className="space-y-5">
            {BOARD_LANES.map(lane => {
              const inLane = filteredBoard.filter(p => p.status === lane.status);
              if (inLane.length === 0) return null;
              return (
                <div key={lane.status}>
                  <div className="flex items-center gap-2 mb-2 sticky top-0 bg-ink/80 backdrop-blur py-1 z-[1]">
                    <Pill label={PACK_LABEL[lane.status]} tone={PACK_TONE[lane.status]} small/>
                    <span className="text-[12px] text-text-muted">{lane.label}</span>
                    <span className="text-[11px] font-mono text-text-dim">· {inLane.length}</span>
                  </div>
                  <div className="space-y-2">
                    {inLane.map(p => <PackCard key={p.pack_id} pack={p} forceReview />)}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : tab === "sent" ? (
        <div className="space-y-2">
          {sentSubmissions
            .filter(s => {
              if (!search.trim()) return true;
              const q = search.trim().toLowerCase();
              return (s.submission_number?.toLowerCase().includes(q))
                  || (s.title?.toLowerCase().includes(q) ?? false)
                  || (s.project?.project_code.toLowerCase().includes(q) ?? false)
                  || (s.project?.project_name.toLowerCase().includes(q) ?? false);
            })
            .map(s => <SubmissionCard key={s.submission_id} submission={s} />)}
          {sentSubmissions.length === 0 && (
            <Card>
              <EmptyState
                title="No submissions sent yet"
                description="When you build a submission from the basket and send to client, it'll show here."
                icon={Send}
              />
            </Card>
          )}
        </div>
      ) : filteredPacks.length === 0 ? (
        <Card>
          <EmptyState
            title={emptyTitle(tab)}
            description={emptyDescription(tab, isManager)}
            icon={tab === "awaiting" ? CheckCircle2 : Camera}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredPacks.map(p => <PackCard key={p.pack_id} pack={p} />)}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SubmissionCard — T-220 v2 (Acceptance Centre layer). Renders one row of the
// "Sent to client" tab. Click → opens the public /accept/<token> URL in a new
// tab so manager sees what client sees.
// -----------------------------------------------------------------------------
function SubmissionCard({ submission: s }: { submission: SubmissionRow }) {
  const statusTone =
    s.status === "signed"     ? "approved" :
    s.status === "downloaded" ? "approved" :
    s.status === "opened"     ? "pending"  :
    s.status === "sent"       ? "pending"  :
    s.status === "expired"    ? "rejected" :
    s.status === "cancelled"  ? "rejected" :
                                 "open";

  const expiresIn = (() => {
    if (!s.expires_at) return null;
    const ms = new Date(s.expires_at).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
    return days === 1 ? "expires tomorrow" : `expires in ${days} days`;
  })();

  const lastEvent =
    s.signed_at             ? `signed ${rel(s.signed_at)}`
    : s.first_downloaded_at ? `downloaded ${rel(s.first_downloaded_at)}`
    : s.first_opened_at     ? `opened ${rel(s.first_opened_at)}`
    : s.sent_at             ? `sent ${rel(s.sent_at)}`
                            : "draft";

  const acceptUrl = s.magic_token ? `/accept/${s.magic_token}` : null;

  return (
    <Card>
      <div className="p-3 md:p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
          <Send size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[12px] text-accent">{s.submission_number}</span>
            <span className="text-[13px] text-text truncate">{s.title ?? "(untitled submission)"}</span>
            <Pill label={s.status} tone={statusTone} small/>
          </div>
          <div className="text-[11px] text-text-muted mt-1 flex items-center gap-3 flex-wrap">
            {s.project && (
              <span className="font-mono">{s.project.project_code} · {s.project.project_name}</span>
            )}
            <span>{lastEvent}</span>
            {expiresIn && (
              <span className={expiresIn === "expired" ? "text-warn" : ""}>{expiresIn}</span>
            )}
          </div>
        </div>
        {acceptUrl && (
          <a
            href={acceptUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-accent hover:text-accent-bright inline-flex items-center gap-1 self-center"
          >
            View as client →
          </a>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------

function TabBtn({ label, active, onClick, count }: {
  label: string; active: boolean; onClick: () => void; count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded text-[12px] transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
        active
          ? "bg-accent/15 text-accent border border-accent/30"
          : "text-text-muted hover:text-text border border-transparent"
      }`}
    >
      {label}
      {typeof count === "number" && count > 0 && (
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
          active ? "bg-accent/20 text-accent" : "bg-ink text-text-muted"
        }`}>{count}</span>
      )}
    </button>
  );
}

// -----------------------------------------------------------------------------

function PackCard({ pack, forceReview = false }: { pack: PackRow & { last_asset_at?: string | null }; forceReview?: boolean }) {
  // For open packs, prefer last asset upload time over 'created' so managers
  // see live activity ("photos updated 2min ago" beats "created 4 days ago").
  const lastAssetAt = (pack as any).last_asset_at as string | null | undefined;
  const lastActivity =
    pack.accepted_at ? `accepted ${rel(pack.accepted_at)}`
    : pack.defects_raised_at ? `defects raised ${rel(pack.defects_raised_at)}`
    : pack.submitted_at ? `submitted ${rel(pack.submitted_at)}`
    : (pack.status === "open" || pack.status === "defects_raised") && lastAssetAt
        ? `last upload ${rel(lastAssetAt)}`
    : `created ${rel(pack.created_at)}`;
  const showAssetCount = (pack.status === "open" || pack.status === "defects_raised") && typeof pack.asset_count === "number";

  // Status drives which surface the row links to:
  //   * open / defects_raised   → project HOP pack overview (the WORK surface,
  //                                with tappable categories + upload affordance).
  //                                This is what field crew need on mobile.
  //   * submitted / accepted    → Acceptance Centre pack detail (the REVIEW
  //                                surface — read-only photo grid + manager actions).
  // Same logic on desktop + mobile, no viewport check needed.
  // Board (manager tracking) keeps you IN the centre — click opens the review
  // surface, not the project. The per-tab lists keep the work-surface link for
  // in-progress packs so field crew land on the capture screen.
  const isWorkInProgress = pack.status === "open" || pack.status === "defects_raised";
  const linkTo = (!forceReview && isWorkInProgress && pack.project?.project_id)
    ? `/projects/${pack.project.project_id}/hop/${pack.pack_id}`
    : `/acceptance/${pack.pack_id}`;

  return (
    <Link
      to={linkTo}
      className="block group"
    >
      <Card>
        <div className="p-3 md:p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
            <FolderKanban size={16}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[11px] font-mono text-text-muted">
                  {pack.project?.project_code ?? "—"} · {pack.project?.project_name ?? "Unknown project"}
                  {pack.project?.site?.site_name && <span className="text-text-dim"> · {pack.project.site.site_name}</span>}
                </div>
                <div className="text-text font-medium mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{pack.pack_number}</span>
                  {pack.label_prefix && (
                    <span className="text-[10px] font-mono text-text-dim">· {pack.label_prefix}</span>
                  )}
                  {pack.template && (
                    <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">
                      {pack.template.template_code}
                    </span>
                  )}
                </div>
                {pack.title && (
                  <div className="text-[12px] text-text-muted mt-0.5 line-clamp-1">{pack.title}</div>
                )}
                <div className="text-[11px] text-text-dim mt-1">
                  {showAssetCount && (
                    <span className="text-accent font-medium">{pack.asset_count} photo{pack.asset_count === 1 ? "" : "s"}</span>
                  )}
                  {showAssetCount && <span className="mx-1.5">·</span>}
                  {lastActivity}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Pill label={PACK_LABEL[pack.status]} tone={PACK_TONE[pack.status]} small />
                <ChevronRight size={16} className="text-text-dim group-hover:text-accent transition-colors" />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

// -----------------------------------------------------------------------------

function rel(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return format(parseISO(iso), "dd MMM yyyy");
  }
}

function emptyTitle(tab: TabKey): string {
  switch (tab) {
    case "open":     return "No packs in progress";
    case "mine":     return "You haven't uploaded to any packs yet";
    case "awaiting": return "Nothing waiting for review";
    case "recent":   return "No packs accepted in the last 30 days";
  }
}

function emptyDescription(tab: TabKey, isManager: boolean): string {
  switch (tab) {
    case "open":     return isManager
      ? "Nothing live right now. As soon as field crew create a pack and start uploading photos, it'll show here in real time."
      : "No active HOP packs assigned to your projects. Ask your manager to create one before heading to site.";
    case "mine":     return "Once you start capturing photos for a pack, it'll appear here.";
    case "awaiting": return isManager
      ? "All clean — no submissions sitting in the queue. Field crew haven't pushed anything new for review."
      : "Field crew haven't submitted any packs for manager review.";
    case "recent":   return "Past 30 days has been quiet.";
  }
}
